import _ from 'lodash';
import {
  AccountData,
  Balances,
  CashieringProfileState,
  CashieringRoute,
  Distribution,
  Position,
  TaxWithholdingType,
  TransferBankIdentifierType,
  TransferData,
} from '@tradingblock/types';
import { formatNumber, parseNumber } from '@tradingblock/components';
import { CashieringViewFormValues } from './useCashieringView';
import { getTransferBankIdentifierType } from '../../../../utilities/cashiering';

interface ValidationErrors {
  required: string;
  invalid?: string;
  max?: string;
}

export const validateAmount = (
  amount: string | number | undefined,
  errors: ValidationErrors,
  field: string,
  max?: number,
  availableForWithdrawal?: number
) => {
  if (!amount) {
    return { [field]: errors.required };
  }

  const amountNumber = parseNumber(`${amount}`);
  if (_.isNaN(amountNumber) || amountNumber <= 0) {
    return { [field]: errors.invalid || 'Invalid amount' };
  }
  if (max && amountNumber > max) {
    return {
      [field]:
        errors.max ||
        `We were not able to process your request. Maximum daily limit is $${formatNumber(max, {
          integer: true,
          format: true,
        })}.`,
    };
  }

  if (
    availableForWithdrawal !== null &&
    availableForWithdrawal !== undefined &&
    amountNumber > availableForWithdrawal
  ) {
    return {
      [field]: `Amount cannot be greater than your total balance (${formatNumber(availableForWithdrawal, {
        currency: true,
      })}).`,
    };
  }

  return {};
};

export const validateTransferAmount = (
  values: CashieringViewFormValues,
  route: CashieringRoute,
  hasSubAccounts: boolean,
  subAccountBalances?: { [subAccountId: string]: Balances },
  availableForWithdrawal?: number
) => {
  if (!values.transfer || values.transfer.disbursementType !== 'FullBalance') {
    let amountErrors = validateAmount(
      values.transfer && values.transfer.amount,
      {
        required: `Please enter the amount to ${route}.`,
        invalid: `Please enter a valid amount to ${route}.`,
      },
      'transfer.amount',
      undefined,
      availableForWithdrawal
    );

    if (
      hasSubAccounts &&
      subAccountBalances !== undefined &&
      values.transfer !== undefined &&
      values.transfer.subAccountsValues !== undefined
    ) {
      amountErrors = {
        ...amountErrors,
        ...Object.keys(values.transfer.subAccountsValues).reduce(
          (errors, subAccountId) => {
            let error = {};
            if (
              values.transfer !== undefined &&
              values.transfer.subAccountsValues !== undefined &&
              values.transfer.subAccountsValues[subAccountId] !== undefined &&
              subAccountBalances[subAccountId] !== undefined
            ) {
              if (
                (values.transfer.subAccountsValues[subAccountId] as number) >
                subAccountBalances[subAccountId].Balances.WithdrawalAmount
              ) {
                error = {
                  [`transfer.subAccountsValues.${subAccountId}`]: `You don't have sufficient funds in this sub-account to withdraw ${formatNumber(
                    values.transfer.subAccountsValues[subAccountId],
                    {
                      currency: true,
                    }
                  )}. Please adjust amount.`,
                };
              }
            }
            return {
              ...errors,
              ...error,
            };
          },
          {} as { [field: string]: string }
        ),
      };
    }
    if (!_.isEmpty(amountErrors)) {
      return amountErrors;
    }

    const { transfer } = values;
    if (hasSubAccounts && (!transfer || !transfer.subAccountId)) {
      return {
        'transfer.subAccountId': 'Please select a sub-account.',
      };
    }

    if (
      transfer &&
      transfer.amount &&
      availableForWithdrawal !== null &&
      availableForWithdrawal !== undefined &&
      typeof transfer.amount === 'number' &&
      transfer.amount > availableForWithdrawal
    ) {
      return {
        'transfer.amount': `Amount cannot be greater than your total balance (${formatNumber(availableForWithdrawal, {
          currency: true,
        })}).`,
      };
    } else if (
      transfer &&
      transfer.amount &&
      availableForWithdrawal !== null &&
      availableForWithdrawal !== undefined &&
      typeof transfer.amount === 'string'
    ) {
      const amountNumber = parseNumber(`${transfer.amount}`);

      if (amountNumber > availableForWithdrawal) {
        return {
          'transfer.amount': `Amount cannot be greater than your total balance (${formatNumber(availableForWithdrawal, {
            currency: true,
          })}).`,
        };
      }
    }
  }
  return {};
};

export const validateIraContribution = (
  values: CashieringViewFormValues,
  cashieringProfile: CashieringProfileState
) => {
  const { transfer } = values;
  const { iraConstraints } = cashieringProfile;
  const { contribution } = iraConstraints || {};
  const { ach } = contribution || {};
  const { allowedTypes } = ach || {};
  if (transfer && !transfer.iraContribution && allowedTypes && allowedTypes.length > 0) {
    return { ['transfer.iraContribution.type']: 'Please select the IRA contribution type.' };
  }
};

// Helper function to validate the number format
const isValidNumberFormat = (value: string | number, withholdingType: 'Percentage' | 'Fixed') => {
  // Convert value to string if it's a number
  const valueStr = value.toString();

  // Regex to ensure value is a valid number with at most one decimal point
  // For percentage, allow up to 4 decimal places
  // For fixed amount, allow up to 2 decimal places
  const regex = withholdingType === 'Percentage' ? /^(\d+|\.\d+|\d+\.\d{1,4})$/ : /^(\d+|\.\d+|\d+\.\d{1,2})$/;

  return regex.test(valueStr);
};

export const validateIraDistribution = (
  values: CashieringViewFormValues,
  isIRA: boolean | undefined,
  cashieringProfile: CashieringProfileState
) => {
  const { transfer } = values;
  const { iraDistribution } = transfer || {};
  const { reason } = iraDistribution || {};

  const { iraConstraints } = cashieringProfile;
  const { federalTaxWithholdingRequirements, stateTaxWithholdingRequirements } = iraConstraints || {};
  const { distribution } = iraConstraints
    ? iraConstraints
    : {
        distribution: undefined,
      };
  const { mechanism } = transfer ? transfer : { mechanism: undefined };
  const formattedMechanism = mechanism ? mechanism.toLowerCase() : undefined;
  const distributionReasons =
    distribution && mechanism && formattedMechanism
      ? distribution[formattedMechanism as keyof Distribution].validReasons
      : undefined;
  const selectedReasonObject = distributionReasons && distributionReasons.find((r: any) => r.reason === reason);

  // variable to determine if both federal and state tax withHoldingOption is disabled at the profile level
  const isStateWithholdingOptionEnabledForProfile = stateTaxWithholdingRequirements
    ? stateTaxWithholdingRequirements.withholdingOption !== 'Disabled'
    : false;
  const isFederalWithholdingOptionEnabledForProfile = federalTaxWithholdingRequirements
    ? federalTaxWithholdingRequirements.withholdingOption !== 'Disabled'
    : false;

  // variables to determine if the federal/state tax withholding options are enabled at the valid reason level
  const isStateWithholdingEnabledForReason = selectedReasonObject && selectedReasonObject.stateTaxWithholdingEnabled;
  const isFederalWithholdingEnabledForReason =
    selectedReasonObject && selectedReasonObject.federalTaxWithholdingEnabled;
  const isDateOfDeathRequired = selectedReasonObject && selectedReasonObject.dateOfDeathRequired;

  let transferAmount = transfer && transfer.amount;

  // if transfer amount is a string, we need to parse it to a number
  // if it is a number, we can use it as is
  if (typeof transferAmount === 'string') {
    transferAmount = parseNumber(transferAmount);
  }

  if (!isIRA || !transfer || !transferAmount) {
    return {};
  }

  // check distribution reason
  if (!transfer || !transfer.iraDistribution || !transfer.iraDistribution.reason) {
    return { 'transfer.iraDistribution.reason': 'Please select the distribution reason.' };
  }

  // check date of death if required
  // if it is required the format will be as an object { month: number, day: number, year: number }
  // all values must be present
  // the date of death should also be in the past
  if (isDateOfDeathRequired) {
    const dateOfDeath = transfer.iraDistribution.dateOfDeath;

    // Type narrowing to check if dateOfDeath is an object with the required properties
    if (
      !dateOfDeath ||
      (typeof dateOfDeath === 'object' && (!dateOfDeath.month || !dateOfDeath.day || !dateOfDeath.year))
    ) {
      return {
        'transfer.iraDistribution.dateOfDeath': 'Please enter the date of death.',
      };
    }

    if (typeof dateOfDeath === 'object') {
      // Handle the case where dateOfDeath is an object
      const dateOfDeathValue = new Date(dateOfDeath.year, dateOfDeath.month - 1, dateOfDeath.day);

      if (dateOfDeathValue > new Date()) {
        return {
          'transfer.iraDistribution.dateOfDeath': 'Date of death must be in the past.',
        };
      }
    } else if (typeof dateOfDeath === 'string') {
      // Handle the case where dateOfDeath is a string
      const dateOfDeathValue = new Date(dateOfDeath);

      if (isNaN(dateOfDeathValue.getTime())) {
        return {
          'transfer.iraDistribution.dateOfDeath': 'Invalid date format.',
        };
      }

      if (dateOfDeathValue > new Date()) {
        return {
          'transfer.iraDistribution.dateOfDeath': 'Date of death must be in the past.',
        };
      }
    }
  }

  // check the tax withholding type if either federal or state tax withholding is required
  if (isStateWithholdingEnabledForReason || isFederalWithholdingEnabledForReason) {
    if (
      !transfer.iraDistribution.federalTaxWithholding ||
      (transfer.iraDistribution.federalTaxWithholding && !transfer.iraDistribution.federalTaxWithholding.valueType)
    ) {
      return {
        'transfer.iraDistribution.federalTaxWithholding.type': 'Please select the tax withholding type.',
      };
    }
  }

  const withholdingType =
    transfer.iraDistribution.federalTaxWithholding && transfer.iraDistribution.federalTaxWithholding.valueType;

  // Validate federal tax withholding value format
  let federalTaxWithholdingValue: number | string | undefined =
    transfer &&
    transfer.iraDistribution &&
    transfer.iraDistribution.federalTaxWithholding &&
    transfer.iraDistribution.federalTaxWithholding.value;

  // if federal tax withholding value is a string, we need to parse it to a number
  // if it is a number, we can use it as is
  if (typeof federalTaxWithholdingValue === 'string') {
    federalTaxWithholdingValue = parseNumber(federalTaxWithholdingValue);
  }

  if (
    federalTaxWithholdingValue &&
    withholdingType &&
    !isValidNumberFormat(federalTaxWithholdingValue, withholdingType)
  ) {
    return {
      'transfer.federalTax': 'Federal tax withholding must be a valid number with no more than two decimal places.',
    };
  }

  // Validate state tax withholding value format
  let stateTaxWithholdingValue: number | string | undefined =
    transfer &&
    transfer.iraDistribution &&
    transfer.iraDistribution.stateTaxWithholding &&
    transfer.iraDistribution.stateTaxWithholding.value;

  // if state tax withholding value is a string, we need to parse it to a number
  // if it is a number, we can use it as is
  if (typeof stateTaxWithholdingValue === 'string') {
    stateTaxWithholdingValue = parseNumber(stateTaxWithholdingValue);
  }

  if (stateTaxWithholdingValue && withholdingType && !isValidNumberFormat(stateTaxWithholdingValue, withholdingType)) {
    return {
      'transfer.stateTax': 'State tax withholding must be a valid number with no more than two decimal places.',
    };
  }

  // review federal tax withholding requirements
  if (federalTaxWithholdingRequirements) {
    if (
      federalTaxWithholdingRequirements.withholdingOption === 'Mandatory' &&
      transfer &&
      (transfer.iraDistribution &&
        transfer.iraDistribution.federalTaxWithholding &&
        !transfer.iraDistribution.federalTaxWithholding.value) &&
      isFederalWithholdingOptionEnabledForProfile &&
      isFederalWithholdingEnabledForReason
    ) {
      return { 'transfer.federalTax': 'Please enter the federal tax withholding amount for this distribution.' };
    }
    if (
      federalTaxWithholdingRequirements.requiredMinimum === 'Percentage' &&
      federalTaxWithholdingRequirements.requiredMinimumPercent
    ) {
      // check to ensure that the value entered is greater than or equal to the minimum percentage * the distribution amount
      const federalTaxWithholdingValue =
        transfer &&
        transfer.iraDistribution &&
        transfer.iraDistribution.federalTaxWithholding &&
        transfer.iraDistribution.federalTaxWithholding.value;

      // if withholdingType is a percentage, we need to ensure that the value is greater than or equal to the minimum percentage
      if (withholdingType === TaxWithholdingType.Percent) {
        const federalRequirement = federalTaxWithholdingRequirements.requiredMinimum;
        const federalRequiredPercentage = federalTaxWithholdingRequirements.requiredMinimumPercent;

        if (
          federalTaxWithholdingValue &&
          federalRequiredPercentage &&
          federalTaxWithholdingValue * 100 < federalRequiredPercentage
        ) {
          return {
            'transfer.federalTax': `Federal tax withholding must be greater than or equal to ${federalRequirement}% of the distribution amount.`,
          };
        }
      }
    }
  }

  // review state tax withholding requirements
  if (stateTaxWithholdingRequirements) {
    if (
      stateTaxWithholdingRequirements.withholdingOption === 'Mandatory' &&
      stateTaxWithholdingRequirements.requiredMinimum !== 'PercentageOfFederal' &&
      transfer &&
      (transfer.iraDistribution &&
        transfer.iraDistribution.stateTaxWithholding &&
        !transfer.iraDistribution.stateTaxWithholding.value) &&
      isStateWithholdingEnabledForReason &&
      isStateWithholdingOptionEnabledForProfile &&
      stateTaxWithholdingRequirements &&
      stateTaxWithholdingRequirements.requiredMinimum !== 'NoMinimum'
    ) {
      return { 'transfer.stateTax': 'Please enter the state tax withholding amount for this distribution.' };
    }

    const stateTaxWithholdingValue =
      transfer &&
      transfer.iraDistribution &&
      transfer.iraDistribution.stateTaxWithholding &&
      transfer.iraDistribution.stateTaxWithholding.value;

    if (stateTaxWithholdingRequirements.requiredMinimum === 'Percentage') {
      const stateRequiredPercentage = stateTaxWithholdingRequirements.requiredMinimumPercent * 100;

      if (withholdingType === TaxWithholdingType.Percent) {
        if (
          stateTaxWithholdingValue &&
          stateRequiredPercentage &&
          stateTaxWithholdingValue * 100 < stateRequiredPercentage
        ) {
          return {
            'transfer.stateTax': `State tax withholding must be greater than or equal to ${stateRequiredPercentage}% of the distribution amount.`,
          };
        }
      }
      if (withholdingType === TaxWithholdingType.Fixed) {
        const stateRequiredAmount = parseFloat(
          (transferAmount * stateTaxWithholdingRequirements.requiredMinimumPercent).toFixed(2)
        );

        if (stateTaxWithholdingValue && stateRequiredAmount && stateTaxWithholdingValue < stateRequiredAmount) {
          return {
            'transfer.stateTax': `State tax withholding must be greater than or equal to ${formatNumber(
              stateRequiredAmount,
              {
                currency: true,
              }
            )} of the distribution amount.`,
          };
        }
      }
    }

    // Handling 'PercentageOfFederal' for state tax withholding requirements
    if (stateTaxWithholdingRequirements.requiredMinimum === 'PercentageOfFederal') {
      // Ensure federal withholding is set, is greater than 0, and of the correct type
      const federalTaxWithholding = transfer.iraDistribution && transfer.iraDistribution.federalTaxWithholding;

      if (!federalTaxWithholding || !federalTaxWithholding.value || federalTaxWithholding.value === 0) {
        // If federal withholding is voluntary and 0, no state withholding is required
        return {};
      }

      // Calculate the minimum state withholding based on the federal withholding value
      const federalWithholdingValue = federalTaxWithholding.value;
      const stateRequiredPercentageOfFederal = stateTaxWithholdingRequirements.requiredMinimumPercent || 0;

      if (withholdingType === TaxWithholdingType.Percent) {
        const federalAmount = (federalWithholdingValue / 100) * transferAmount;
        const percentageOfFederalValue = stateRequiredPercentageOfFederal * federalAmount;
        const percentageOfFederalPercentValue = (percentageOfFederalValue / transferAmount) * 100;

        if (
          (stateTaxWithholdingValue && stateTaxWithholdingValue < percentageOfFederalPercentValue) ||
          !stateTaxWithholdingValue ||
          stateTaxWithholdingValue === 0
        ) {
          return {
            'transfer.stateTax': `State tax withholding must be greater than or equal to ${formatNumber(
              percentageOfFederalValue,
              { currency: true }
            )}, which is ${percentageOfFederalPercentValue}% of the distribution or ${stateRequiredPercentageOfFederal *
              100}% of the federal tax withholding.`,
          };
        }
      } else if (withholdingType === TaxWithholdingType.Fixed) {
        const requiredStateAmount = parseFloat((federalWithholdingValue * stateRequiredPercentageOfFederal).toFixed(2));

        if (
          (stateTaxWithholdingValue && stateTaxWithholdingValue < requiredStateAmount) ||
          !stateTaxWithholdingValue ||
          stateTaxWithholdingValue === 0
        ) {
          return {
            'transfer.stateTax': `State tax withholding must be greater than or equal to ${formatNumber(
              requiredStateAmount,
              {
                currency: true,
              }
            )}, which is ${stateRequiredPercentageOfFederal * 100}% of the federal tax withholding.`,
          };
        }
      }
    }

    // Verify that the state and federal tax withholding values do not exceed the distribution amount for fixed withholding types, and do not exceed 100% for percentage-based withholding, either individually or as a combined sum
    const stateTaxWithholdingValueNumber =
      typeof stateTaxWithholdingValue === 'string' ? parseFloat(stateTaxWithholdingValue) : stateTaxWithholdingValue;
    const federalTaxWithholdingValueNumber =
      typeof federalTaxWithholdingValue === 'string'
        ? parseFloat(federalTaxWithholdingValue)
        : federalTaxWithholdingValue;

    if (withholdingType === TaxWithholdingType.Fixed) {
      // Calculate the sum of the state and federal tax withholding values
      const totalWithholding =
        (stateTaxWithholdingValueNumber ? stateTaxWithholdingValueNumber : 0) +
        (federalTaxWithholdingValueNumber ? federalTaxWithholdingValueNumber : 0);

      // Check if the total withholding exceeds the distribution amount
      if (totalWithholding > transferAmount) {
        return {
          'transfer.stateTax': `The total tax withholding amount (${formatNumber(totalWithholding, {
            currency: 'USD',
          })}) cannot exceed the distribution amount (${formatNumber(transferAmount, { currency: 'USD' })}).`,
        };
      }
    }

    if (withholdingType === TaxWithholdingType.Percent) {
      // Calculate the sum of the state and federal tax withholding percentages
      const totalWithholdingPercentage =
        (stateTaxWithholdingValueNumber ? stateTaxWithholdingValueNumber : 0) +
        (federalTaxWithholdingValueNumber ? federalTaxWithholdingValueNumber : 0);
      // Check if the total withholding exceeds 100%
      if (totalWithholdingPercentage > 100) {
        return {
          'transfer.stateTax': `The total tax withholding percentage (${totalWithholdingPercentage.toLocaleString(
            'en-US',
            { maximumFractionDigits: 2 }
          )}%) cannot exceed 100%.`,
        };
      }
    }
  }
};

export const validatePositions = (values: CashieringViewFormValues, hasPositions: boolean, positions: Position[]) => {
  const { transfer } = values;
  const positionSymbols = positions.map(p => p.Symbol);
  const uniquePositionSymbols = _.uniq(positionSymbols).join(', ');

  if (hasPositions && transfer && transfer.disbursementType === 'FullBalance' && transfer.closeAccount) {
    return {
      'transfer.closeAccount': `Unable to close account with open positions. Please review your positions and try again: ${uniquePositionSymbols}.`,
    };
  }
};

export const validateTransferMechanism = (values: CashieringViewFormValues, route: CashieringRoute) => {
  if (
    !values.transfer ||
    !values.transfer.mechanism ||
    (values.transfer.mechanism === 'Ach' && !values.transfer.achRelationshipId) ||
    (values.transfer.mechanism === 'Wire' && !values.transfer.instructionId)
  ) {
    return {
      [`transfer.${
        values.transfer && values.transfer.mechanism === 'Wire' ? 'instructionId' : 'achRelationshipId'
      }`]: `Please select how you would like to ${
        route === CashieringRoute.Withdraw ? 'receive' : 'deposit'
      } the funds.`,
    };
  }
  if (values.transfer.mechanism === 'Check') {
    if (!values.transfer.nameOnAccount) {
      return { 'transfer.nameOnAccount': 'Please enter the name as it appears on bank account.' };
    }
    if (!values.transfer.deliveryMethod) {
      return { 'transfer.deliveryMethod': 'Please select the check delivery method.' };
    }
  }

  // if (values.check) {
  //   if (values.check.thirdParty) {
  //     const thirdPartyAddress = values.check.thirdPartyAddress || {};
  //     if (!thirdPartyAddress.inCareOf) {
  //       return { 'check.thirdPartyAddress.inCareOf': 'Please enter the "in care of" name' };
  //     }
  //     if (!thirdPartyAddress.address1) {
  //       return { 'check.thirdPartyAddress.address1': 'Please enter the mailing street name' };
  //     }
  //     if (!thirdPartyAddress.city) {
  //       return { 'check.thirdPartyAddress.city': 'Please enter the mailing city' };
  //     }
  //     if (!thirdPartyAddress.state) {
  //       return { 'check.thirdPartyAddress.state': 'Please enter the mailing state or province' };
  //     }
  //     if (!thirdPartyAddress.postalCode) {
  //       return { 'check.thirdPartyAddress.postalCode': 'Please enter the mailing postal code' };
  //     }
  //     if (!thirdPartyAddress.country) {
  //       return { 'check.thirdPartyAddress.country': 'Please enter the mailing country' };
  //     }
  //   }
  // }

  return {};
};

export const validateTransferAchRelationship = (account: AccountData | undefined) => {
  if (account && account.approvalMethod === 'Plaid') {
    if (!account || !account.plaidAccountId) {
      return { 'account.plaidAccountId': 'Please select the bank account.' };
    }
    if (!account.nickName) {
      return { 'account.nickName': 'Please enter the account nickname.' };
    }
  } else {
    if (!account || !account.bankAccountType) {
      return { 'account.bankAccountType': 'Please select the type of bank account.' };
    }
    if (!account.bankRoutingNumber) {
      return { 'account.bankRoutingNumber': 'Please enter the bank routing account.' };
    }
    if (!account.bankName) {
      return { 'account.bankName': 'Please enter the bank name.' };
    }
    if (!account.bankAccount) {
      return { 'account.bankAccount': 'Please enter the bank account number.' };
    }
    if (!account.bankAccountOwnerName) {
      return { 'account.bankAccountOwnerName': 'Please enter the account owner name.' };
    }
    if (!account.nickName) {
      return { 'account.nickName': 'Please enter the account nickname.' };
    }
  }

  return {};
};

export const validateTransferAchRelationshipOptions = (
  account: AccountData | undefined,
  transfer: TransferData | undefined
) => {
  if (!account || !account.approvalMethod) {
    return { 'account.approvalMethod': 'Please select an approval method.' };
  }

  return {};
};

export const validateTransferRecipientBank = (account: AccountData | undefined) => {
  const bankIdentifierType = account && getTransferBankIdentifierType(account);
  if (!account || !account.name) {
    return { 'account.name': 'Please enter the bank name.' };
  }
  if (!account.identifier) {
    return {
      'account.identifier': `Please enter the bank ${
        bankIdentifierType === TransferBankIdentifierType.BIC ? 'BIC/SWIFT Code' : 'ABA/Routing Number'
      }.`,
    };
  } else {
    if (bankIdentifierType === TransferBankIdentifierType.ABA && !validateABARouting(account.identifier)) {
      return { 'account.identifier': 'Please enter a valid bank ABA/Routing Number.' };
    }
    if (bankIdentifierType === TransferBankIdentifierType.BIC && !validateBICSWIFT(account.identifier)) {
      return { 'account.identifier': 'Please enter a valid bank BIC/SWIFT Code.' };
    }
  }
  if (!account.accountNumber) {
    return { 'account.accountNumber': 'Please enter the bank account.' };
  } else if (!validateAccountNumber(account.accountNumber)) {
    return { 'account.accountNumber': 'Please enter a valid bank account number.' };
  }

  if (!account.nameOnAccount) {
    return { 'transfer.nameOnAccount': 'Please enter the name as it appears on bank account.' };
  }
  if (!account.nickName) {
    return { 'account.nickName': 'Please enter the account nickname.' };
  }

  if (bankIdentifierType === TransferBankIdentifierType.BIC) {
    if (!account.city) {
      return { 'account.city': 'Please enter the bank city.' };
    }
    if (!account.stateProvince) {
      return { 'account.stateProvince': 'Please enter the bank state or province.' };
    }
    if (!account.postalCode) {
      return { 'account.postalCode': 'Please enter the bank postal code.' };
    }
    if (!account.country) {
      return { 'account.country': 'Please enter the bank country.' };
    }
  }

  // need to validate intermediary fields...

  return {};
};

const validateABARouting = (code: string) => {
  if (code.length === 9) {
    // Run through each digit and calculate the total.
    let n = 0;
    for (let i = 0; i < code.length; i += 3) {
      n += parseInt(code.charAt(i), 10) * 3 + parseInt(code.charAt(i + 1), 10) * 7 + parseInt(code.charAt(i + 2), 10);
    }
    // If the resulting sum is an even multiple of ten (but not zero), the aba routing number is good.
    if (n !== 0 && n % 10 === 0) {
      return true;
    }
  }
  return false;
};

const validateBICSWIFT = (code: string) => {
  /*
  Example: AAAABBCC123
  AAAA -- Bank code [A-Z]
  BB -- Country code [A-Z]
  CC -- Location code [0-9A-Z]
  123 -- Branch code [0-9A-Z]
  */
  return /^([A-Z]{4})([A-Z]{2})([0-9A-Z]{2})([0-9A-Z]{3})$/.test(code);
};

const validateAccountNumber = (accountNumber: any) => {
  return /^[0-9]*$/.test(accountNumber);
};
